import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isNullish } from '@sas/nova-core/typeguards';
import { DisclosureExpand } from '@sas/react-svg';
import cx from 'classnames';
import { BrowserName, getBrowserName } from '../../../browser-name';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { useMounted } from '../../../hooks/useMounted';
import { useResizeObserverRef } from '../../../hooks/useResizeObserver';
import { useIsRTL } from '../../../locale';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { renderSlotWithComponentFallback } from '../../../slot';
import { getOSName, OSName } from '../../../util';
import { convertCSSSizeToNumber } from '../../../util/dom';
import { getScrollbarSize } from '../../../util/dom/getScrollbarSize';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { isOptionPopperSeparator } from '../../Dropdown/__internal__/utils/typeguards';
import { Icon } from '../../Icon';
import { useInputValidationAdapter, useNumericValidationAdapter, validateNumericInput } from '../../Input';
import { BaseInput, createInputSuggestionRegex } from '../../Input/__internal__/BaseInput';
import { useEmailValidationAdapter } from '../../Input/hooks/useEmailValidationAdapter';
import { useTelephoneValidationAdapter } from '../../Input/hooks/useTelephoneValidationAdapter';
import { useURLValidationAdapter } from '../../Input/hooks/useURLValidationAdapter';
import { InvisibleText } from '../../InvisibleText';
import bundle from '../NovaComponents-ComboBox-gui-icu.properties';
import cn from '../ComboBox.css.json';

/**
 * @deprecated
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SELECTION_RANGE_DEBOUNCE_DELAY = 0;
const useTextLocalizer = createUseTextLocalizer(bundle);
const useHtmlId = createUseHtmlId('ComboBox');

/**
 * @internal
 */
export const BaseInputBox = /*#__PURE__*/forwardRef(function BaseInputBox({
  label,
  value,
  tooltip,
  className,
  theme,
  font,
  style,
  isOpen,
  items,
  dropdownItems,
  disabled,
  listId,
  ariaActiveDescendant,
  required,
  ariaDescribedBy,
  ariaLabel,
  ariaLabelledBy,
  editable,
  placeholder,
  message,
  testId,
  type = 'text',
  filterType = 'beginsWith',
  status,
  id,
  innerRef,
  renderIcon,
  inputRef,
  allowIntegersOnly = false,
  readOnly,
  enableAutoSelection = true,
  commitOnEnterInDialog = true,
  selectedIndex = -1,
  maxLength,
  enableVirtualScrolling,
  inputContainerWidth,
  setValue,
  setFocusedIndex,
  setSelectedIndex,
  onKeyDown,
  onPress,
  ...props
}, ref) {
  const localize = useTextLocalizer();
  const mountedRef = useMounted();
  const [inputValue, setInputValue] = useState(label ?? value);
  if (!isNullish(maxLength) && inputValue.length > maxLength) {
    setInputValue(inputValue.substring(0, maxLength));
  }
  const suggestionItems = [];
  if (items) {
    for (const item of items) {
      // need to remove separator from the suggestion items
      if (isOptionPopperSeparator(item)) {
        continue;
      } else {
        suggestionItems.push(item);
      }
    }
  }
  const filteredDropdownItems = [];
  if (dropdownItems) {
    for (const item of dropdownItems) {
      // need to remove separator from the suggestion items
      if (isOptionPopperSeparator(item)) {
        continue;
      } else {
        filteredDropdownItems.push(item);
      }
    }
  }
  const [isFocusVisible, setIsFocusVisible] = useState(undefined);
  const refInput = useRef(null);
  const refContainer = useRef(null);
  const buttonRef = useRef(null);
  const suggestionsRef = useRef(null);
  const [shouldHighlight, setShouldHighlight] = useState(false);
  const [comboStatus, setComboStatus] = useState(status);
  const [isValid, setIsValid] = useState(true);
  const [isActive, setIsActive] = useState(false);
  const isDropdownOpening = useRef(false);
  const isAutoWidth = !style?.width;
  const isRTL = useIsRTL();
  const statusClass = status === 'warning' ? cn.warning : cn.error;
  const [typeAhead, setTypeAhead] = useState(false);
  useEffect(() => setComboStatus(status), [status]);
  useEffect(() => {
    // If user has selected item from dropdown, mark ComboBox valid
    if (!isNullish(selectedIndex) && selectedIndex >= 0) setIsValid(true);
  }, [selectedIndex]);
  useEffect(() => {
    setInputValue(value);
  }, [value]);
  useLayoutEffect(() => {
    if (shouldHighlight) {
      const startPos = refInput.current?.selectionStart ?? 0;
      if (type !== 'email' && typeAhead) {
        refInput.current?.setSelectionRange(startPos, refInput.current?.value.length);
      }
      setShouldHighlight(false);
    }
  }, [shouldHighlight, type, typeAhead]);
  useEffect(() => {
    if (label) {
      setShouldHighlight(true);
      setInputValue(label);
    }
  }, [label]);
  useEffect(() => {
    setShouldHighlight(!!isOpen);
    isDropdownOpening.current = !!isOpen;
  }, [isOpen]);
  useEffect(() => {
    const handleInputKeyDown = e => {
      if (!items || readOnly) return;
      const key = e.key;
      switch (key) {
        case 'Escape':
          break;
        case 'Delete':
        case 'Backspace':
        case 'Process':
          setTypeAhead(false);
          break;
        default:
          setTypeAhead(true);
          break;
      }
    };
    if (comboStatus === 'alert' || comboStatus === 'warning') setIsValid(false);else setIsValid(true);
    const input = refInput.current;
    !readOnly && input?.setAttribute('aria-haspopup', 'listbox');
    !readOnly && input?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    input?.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    !readOnly && listId && input?.setAttribute('aria-controls', listId);
    ariaActiveDescendant && input?.setAttribute('aria-activedescendant', ariaActiveDescendant);
    input?.addEventListener('keydown', handleInputKeyDown);
    return () => {
      input?.removeEventListener('keydown', handleInputKeyDown);
    };
  }, [ariaActiveDescendant, disabled, isOpen, isValid, items, listId, readOnly, comboStatus]);
  useImperativeHandle(ref, () => ({
    setIsValid: value => {
      setIsValid(value);
      setComboStatus(undefined);
    },
    onChange: (value, isDropdownChange, focusedIndex, isEscapeKey) => setTimeout(() => {
      onChange({
        value: value ?? '',
        focusedIndex
      }, isDropdownChange, isEscapeKey);
    }, 0),
    focus: () => refInput.current?.focus(),
    isSuggestionsOpen: () => suggestionsRef.current?.isOpen() ?? false,
    closeSuggestions: () => suggestionsRef.current?.closeSuggestions()
  }));
  const invisibleTextId = useHtmlId();
  const comboAriaDescribedBy = required ? [invisibleTextId] : [];
  if (Array.isArray(ariaDescribedBy)) {
    comboAriaDescribedBy.push(...ariaDescribedBy);
  } else if (ariaDescribedBy !== undefined) {
    comboAriaDescribedBy.push(ariaDescribedBy);
  }
  const validateNumericComboBoxInput = useMemo(() => (value, appLocale, isRequired, allowIntegersOnly, isLive, minLength) => {
    if (type !== 'number') {
      setIsValid(true);
      setComboStatus(undefined);
      return {
        isError: false,
        message: '',
        status: undefined,
        values: {
          rawValue: NaN,
          formatValue: ''
        }
      };
    }
    const result = validateNumericInput(value, appLocale, isRequired, allowIntegersOnly, isLive, minLength);
    if (result.isError) {
      setIsValid(false);
      setComboStatus(result.status);
    } else {
      setIsValid(true);
      setComboStatus(result.status);
    }
    return result;
  }, [type]);
  const textValidator = useInputValidationAdapter({
    isRequired: required,
    initialValue: inputValue
  });
  const integerValidator = useNumericValidationAdapter({
    validateInputFunction: validateNumericComboBoxInput,
    isRequired: required,
    allowIntegersOnly
  });
  const urlValidator = useURLValidationAdapter({
    isRequired: required,
    initialValue: inputValue
  });
  const telephoneValidator = useTelephoneValidationAdapter({
    isRequired: required,
    initialValue: inputValue
  });
  const emailValidator = useEmailValidationAdapter({
    isRequired: required,
    initialValue: inputValue
  });
  const onChange = (event, isDropdownChange, isEscapeKey) => {
    if (!mountedRef.current) return;
    if (disabled) return;
    let value;
    let index = -1;
    if (suggestionItems && typeAhead && enableAutoSelection && filterType !== 'contains') {
      const startIndex = event.focusedIndex ? event.focusedIndex : -1;
      const pattern = createInputSuggestionRegex(event.value, 'beginsWith');
      const elem = suggestionItems.find((item, elIndex) => {
        if (pattern?.test(item.text ?? '')) {
          if (event.reason === 'suggestion') {
            // If onChange is from the suggestion list, we need to check for multiple
            // items that are the same, and set index to the correct element
            const text = item.text;
            const firstIndex = suggestionItems.map(obj => obj.text).indexOf(text);
            const lastIndex = suggestionItems.map(obj => obj.text).lastIndexOf(text);
            if (firstIndex !== lastIndex) {
              // We have multiple items that are the same if firstIndex !== lastIndex
              if (elIndex > startIndex) {
                index = elIndex;
                return item;
              }
            } else {
              index = elIndex;
              return item;
            }
          } else {
            const text2 = item.text;
            const itemsArray = suggestionItems.map(obj => obj.text);
            const firstIndex2 = itemsArray.indexOf(text2);
            const lastIndex2 = itemsArray.lastIndexOf(text2);
            if (firstIndex2 !== lastIndex2) {
              // We have multiple items that are the same if firstIndex !== lastIndex
              if (startIndex <= firstIndex2 || elIndex >= startIndex) {
                index = elIndex;
                return item;
              }
            } else {
              if (isDropdownChange) {
                if (text2?.length === event.value.length) {
                  index = elIndex;
                  return item;
                }
              } else {
                index = elIndex;
                return item;
              }
            }
          }
        }
      });
      if (index !== -1) {
        value = elem.text;
        if (maxLength && value && value.length > maxLength) {
          value = value.substring(0, maxLength);
        }
        setValue(value ?? '');
        setInputValue(value ?? '');
        setTimeout(() => {
          if (type !== 'email') {
            refInput.current?.setSelectionRange(isDropdownChange && !isEscapeKey ? 0 : isEscapeKey ? refInput.current?.value.length : event.value.length, refInput.current?.value.length, undefined);
          }
        }, SELECTION_RANGE_DEBOUNCE_DELAY);
      } else {
        value = event.value;
        if (maxLength && value && value.length > maxLength) {
          value = value.substring(0, maxLength);
        }
        setValue(value);
        setInputValue(value);
      }
      setFocusedIndex?.(index);
      setSelectedIndex(index);
    } else {
      value = event.value;
      const startIndex = event.focusedIndex ? event.focusedIndex : -1;
      const itemsArray = filteredDropdownItems.map(obj => obj.text);
      let found = false;
      if ((isDropdownChange || suggestionsRef.current?.isOpen()) && suggestionItems) {
        suggestionItems.find((item, elIndex) => {
          const text = item.text;
          if (!found && text && event.value.indexOf(text) !== -1 && text.length === event.value.length) {
            const firstIndex3 = itemsArray.indexOf(event.value);
            const lastIndex3 = itemsArray.lastIndexOf(event.value);
            if (firstIndex3 !== lastIndex3) {
              // We have multiple items that are the same if firstIndex !== lastIndex
              if (startIndex <= firstIndex3 || elIndex >= startIndex) {
                index = elIndex;
                found = true;
                return;
              }
            } else {
              index = elIndex;
              found = true;
              return;
            }
          }
        });
        if (suggestionItems.length === 0) {
          // Check the dropdownItems if there are no suggestion items
          filteredDropdownItems.find((item, elIndex) => {
            const text = item.text;
            if (!found && text && event.value.indexOf(text) !== -1 && text.length === event.value.length) {
              const firstIndex4 = itemsArray.indexOf(event.value);
              const lastIndex4 = itemsArray.lastIndexOf(event.value);
              if (firstIndex4 !== lastIndex4) {
                // We have multiple items that are the same if firstIndex !== lastIndex
                if (startIndex <= firstIndex4 || elIndex >= startIndex) {
                  index = elIndex;
                  found = true;
                  return;
                }
              } else {
                index = elIndex;
                found = true;
                return;
              }
            }
          });
        }
      } else if (!isDropdownChange) {
        // onChange has been called directly from BaseInput
        let found = false;
        filteredDropdownItems.find((item, elIndex) => {
          const text = item.text;
          if (!found && text && event.value.indexOf(text) !== -1 && text.length === event.value.length) {
            const firstIndex5 = itemsArray.indexOf(event.value);
            const lastIndex5 = itemsArray.lastIndexOf(event.value);
            if (firstIndex5 !== lastIndex5) {
              // We have multiple items that are the same if firstIndex !== lastIndex
              if (startIndex <= firstIndex5 || elIndex >= startIndex) {
                index = elIndex;
                found = true;
                return;
              }
            } else {
              index = elIndex;
              found = true;
              return;
            }
          }
        });
      }
      setShouldHighlight(false);
      if (maxLength && value && value.length > maxLength) {
        value = value.substring(0, maxLength);
      }
      setValue(value);
      setInputValue(value);
      setFocusedIndex?.(index);
      setSelectedIndex(index);
      if (index !== -1) {
        setTimeout(() => {
          if (type !== 'email' && typeAhead) {
            refInput.current?.setSelectionRange(event.value.length, refInput.current?.value.length, undefined);
          }
        }, SELECTION_RANGE_DEBOUNCE_DELAY);
      }
    }
    if (props.validateFunction) {
      props.validateFunction(value ?? '');
    } else {
      if (required) textValidator.handleChange(event);
      if (type === 'url') urlValidator.handleChange(event);else if (type === 'email') emailValidator.handleChange(event);else if (type === 'tel') telephoneValidator.handleChange(event);else if (type === 'number') integerValidator.handleChange(event);
    }
    props.onChange?.({
      ...event,
      suggestion: value,
      isDropdownChange
    });
  };
  const onCommit = event => {
    if (!mountedRef.current) return;
    if (disabled) return;
    if (required && inputValue.trim() === '') {
      setIsValid(false);
      setComboStatus('alert');
    } else {
      switch (comboStatus) {
        case 'alert':
        case 'warning':
          setIsValid(false);
          break;
        default:
          setIsValid(true);
          setComboStatus(undefined);
      }
    }
    setInputValue(event.value);
    if (props.validateFunction) {
      props.validateFunction(event.value ?? '');
    } else {
      if (required) textValidator.handleCommit();
      if (type === 'url') urlValidator.handleCommit();else if (type === 'email') emailValidator.handleCommit();else if (type === 'tel') telephoneValidator.handleCommit();else if (type === 'number') integerValidator.handleCommit();
    }
    if (event.reason === 'enter' || event.reason === 'blur' && !isDropdownOpening.current && !isOpen) props.onCommit?.(event);
    if (event.reason === 'suggestion') {
      const startIndex = event.focusedIndex ?? -1;
      const itemsArray = filteredDropdownItems.map(obj => obj.text);
      const index = filteredDropdownItems?.findIndex((obj, elIndex) => {
        const firstIndex = itemsArray.indexOf(event.value);
        const lastIndex = itemsArray.lastIndexOf(event.value);
        if (firstIndex !== lastIndex) {
          // We have multiple items that are the same if firstIndex !== lastIndex
          if (elIndex > startIndex) {
            if (obj.text === event.value) {
              return elIndex;
            }
          }
        } else {
          return obj.text === event.value;
        }
      }) ?? -1;
      props.onSelected?.(index);
    }
  };
  const onBlur = () => {
    if (!mountedRef.current) return;
    setIsActive(false);
    // Only want to run validation on blur if user has typed value in ComboBox
    if (selectedIndex === -1) {
      if (props.validateFunction) {
        props.validateFunction(inputValue ?? '');
      } else {
        if (required) textValidator.handleBlur();
        if (type === 'url') urlValidator.handleBlur();else if (type === 'email') emailValidator.handleBlur();else if (type === 'tel') telephoneValidator.handleBlur();else if (type === 'number') integerValidator.handleBlur();
      }
    }
    props.onBlur?.();
  };
  const onCancel = () => {
    if (!mountedRef.current) return;
    // Only want to run validation on cancel if user has typed value in ComboBox
    if (selectedIndex === -1) {
      if (props.validateFunction) {
        props.validateFunction(inputValue ?? '');
      } else {
        if (required) textValidator.handleCancel();
        if (type === 'url') urlValidator.handleCancel();else if (type === 'email') emailValidator.handleCancel();else if (type === 'tel') telephoneValidator.handleCancel();else if (type === 'number') integerValidator.handleCancel();
      }
    }

    // ComboBox has its own esc handling but Input will still
    // close the suggestion list if opened. So return undefined.
  };

  const getStatus = () => {
    if (comboStatus) return comboStatus;
    if (status) return status;

    // Only want to get validation status if user has typed value in ComboBox
    if (selectedIndex === -1) {
      let newStatus = undefined;
      if (required) newStatus = textValidator.errors?.status;else if (type === 'url') newStatus = urlValidator.errors?.status;else if (type === 'email') newStatus = emailValidator.errors?.status;else if (type === 'tel') newStatus = telephoneValidator.errors?.status;else if (type === 'number') newStatus = integerValidator.errors?.status;
      if (newStatus) setComboStatus(newStatus);
      return newStatus;
    } else {
      return undefined;
    }
  };
  const getMessage = () => {
    if (message) return message;else if (required) return textValidator.errors?.message;else if (type === 'url') return urlValidator.errors?.message;else if (type === 'email') return emailValidator.errors?.message;else if (type === 'tel') return telephoneValidator.errors?.message;else if (type === 'number') return integerValidator.errors?.message;else return undefined;
  };
  const autoWidth = useCallback(() => {
    if (isAutoWidth && refInput.current?.parentElement && !isNullish(items)) {
      const context = document.createElement('canvas').getContext('2d');
      if (context) {
        let width = 0;
        const getMaxWidth = () => {
          const maxWidth = window.getComputedStyle(refContainer.current?.parentElement).maxWidth;
          if (maxWidth.indexOf('%') > -1) {
            const parentWidth = refContainer.current?.parentElement?.parentElement?.clientWidth;
            if (parentWidth) {
              return parentWidth * (parseInt(maxWidth) * 0.01);
            }
          }
          return parseInt(maxWidth);
        };
        const getTotalWidth = text => context.measureText(text).width;
        const defaultMinWidth = 96;
        const maxWidth = !style?.minWidth ? Math.max(defaultMinWidth, getMaxWidth()) : getMaxWidth();
        const inputStyle = window.getComputedStyle(refInput.current);
        // must be the following order of font-xxx
        const fontFamily = [inputStyle.getPropertyValue('font-style'), inputStyle.getPropertyValue('font-variant'), inputStyle.getPropertyValue('font-weight'), inputStyle.getPropertyValue('font-size'), inputStyle.getPropertyValue('font-family')];
        context.font = fontFamily.join(' ');
        for (const item of items) {
          const label = item.text;
          if (label) width = Math.max(width, getTotalWidth(label));
          if (width >= maxWidth) {
            width = maxWidth;
            break;
          }
        }
        if (width) {
          let scrollbarSize = getScrollbarSize();
          // rtl in FF (NOVACOMMONS-4917) has text-index to handle cut off italic so
          // may need a little wider
          width = width + convertCSSSizeToNumber(inputStyle.textIndent);

          // Firefox on Windows 11 has a new scrollbar style. It's tied to Windows 11's Accessibility
          // setting 'Always show scrollbars'. The setting is set to false by default.
          // The problem is, with the new style they now overlay that scrollbar over the content.
          // This means the scrollbar width is no longer included in the element's offsetWidth property
          // so getScrollbarSize will return 0. In this case, fall back to defaultScrollbarSize (NOVACOMMONS-13793).
          const defaultScrollbarSize = 17;
          const browserName = getBrowserName();
          if (getOSName() === OSName.Win && browserName === BrowserName.Firefox && scrollbarSize === 0) {
            scrollbarSize = defaultScrollbarSize;
          }
          // account for the scrollbar to make sure focus outline can be visible
          width = width + scrollbarSize;
          refInput.current.parentElement.style.width = `${Math.ceil(width)}px`;
          if (!style?.minWidth) {
            const dropDownContainer = refContainer.current?.parentElement;
            const dropdownRect = dropDownContainer.getBoundingClientRect();
            const textRect = refInput.current.getBoundingClientRect();
            const gap = !isRTL ? (textRect.left ?? 0) - (dropdownRect.left ?? 0) : (dropdownRect.right ?? 0) - (textRect.right ?? 0);

            // empty '' to allow default min-width from style class
            dropDownContainer.style.minWidth = width + gap < defaultMinWidth ? 'initial' : '';
          }
        }
      }
    }
  }, [isAutoWidth, items, style?.minWidth, isRTL]);
  const [setParentRef] = useResizeObserverRef(autoWidth);
  useEffect(() => {
    if (isAutoWidth) {
      const maxWidth = window.getComputedStyle(refContainer.current).maxWidth;
      if (maxWidth.indexOf('%') > -1) {
        if (refContainer.current?.parentElement) {
          setParentRef(refContainer.current?.parentElement);
        }
      }
    }
    return () => {
      setParentRef(null);
    };
  }, [innerRef, isAutoWidth, setParentRef]);
  useEffect(autoWidth, [autoWidth]);
  useEffect(() => {
    if (comboStatus) {
      suggestionsRef.current?.closeSuggestions();
    }
  }, [comboStatus]);
  return readOnly ? /*#__PURE__*/_jsx("div", {
    ref: element => {
      refContainer.current = element;
      setRef(innerRef, element);
    },
    className: cx(className, cn.inputBox, isFocusVisible && !isOpen && cn.focusVisible, cn.readOnly, disabled && cn.disabled, (!isValid || status) && statusClass || (type === 'url' && urlValidator.errors?.status === 'alert' || type === 'tel' && telephoneValidator.errors?.status === 'alert' || type === 'email' && emailValidator.errors?.status === 'alert' || type === 'number' && integerValidator.errors?.status === 'alert') && cn.error),
    style: style,
    tabIndex: -1,
    "data-testid": testId,
    role: "group",
    "aria-disabled": disabled,
    children: /*#__PURE__*/_jsx(BaseInput, {
      testId: postfixTestId(testId, 'input'),
      ref: element => {
        refInput.current = element;
        setRef(inputRef, element);
      },
      id: id,
      style: {
        '--input-hover-bg': 'transparent',
        '--input-focus-bg': 'transparent'
      },
      value: inputValue,
      customTooltip: tooltip,
      required: required,
      editable: false,
      disabled: disabled,
      status: getStatus(),
      message: getMessage(),
      ariaLabel: ariaLabel,
      ariaDescribedBy: comboAriaDescribedBy,
      ariaLabelledBy: ariaLabelledBy,
      onChange: () => {},
      hideBorder: true,
      maxLength: maxLength,
      commitOnEnterInDialog: commitOnEnterInDialog
    })
  }) : /*#__PURE__*/_jsxs("div", {
    ref: element => {
      refContainer.current = element;
      setRef(innerRef, element);
    },
    role: "group",
    className: cx(className, cn.inputBox, isFocusVisible && !isOpen && cn.focusVisible, isActive && !disabled && cn.active, disabled && cn.disabled, (!isValid || status) && statusClass || selectedIndex === -1 && (type === 'url' && urlValidator.errors?.status === 'alert' || type === 'tel' && telephoneValidator.errors?.status === 'alert' || type === 'email' && emailValidator.errors?.status === 'alert' || type === 'number' && (integerValidator.errors?.status === 'alert' || comboStatus === 'alert')) && cn.error),
    "data-testid": testId,
    style: style,
    onClick: e => {
      if (e.target !== refInput.current) {
        // the onPress in DropdownDesign toggles
        // the open state
        onPress?.();
        // if clicking on down arrow button, focus should
        // go back to input
        refInput.current?.focus();
        suggestionsRef.current?.closeSuggestions();
      }
    },
    onKeyDown: e => {
      if (e.key === 'Home' || e.key === 'End' || e.key === ' ') {
        e.stopPropagation();
      }
      if (suggestionsRef.current?.isOpen()) {
        e.stopPropagation();
      }
      onKeyDown?.(e);
      setIsFocusVisible(true);
    },
    onKeyUp: props.onKeyUp,
    onMouseDown: event => {
      setIsFocusVisible(false);
      if (event.target !== refInput.current) {
        // down arrow has been clicked to open dropdown
        // set tabIndex to 0 to allow focus so that focus does not move to document.body
        buttonRef.current?.setAttribute('tabindex', '0');
        isDropdownOpening.current = true;
      } else {
        isDropdownOpening.current = false;
      }
    },
    onBlur: event => {
      buttonRef.current?.setAttribute('tabindex', '-1');
      if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget)) {
        // focus is moving to an element outside of this list
        setIsFocusVisible(undefined);
      }
    },
    onFocus: event => {
      if (event.currentTarget.contains(event.target)) {
        // NOTE: onMouseDown runs first, so we assume a tab focused the DropDown div if
        //     | onMouseDown did not run (and set prev to false).
        setIsFocusVisible(prev => prev === undefined ? true : prev);
      }
    },
    children: [required ? /*#__PURE__*/_jsx(InvisibleText, {
      id: invisibleTextId,
      children: localize('required.aria')
    }) : null, /*#__PURE__*/_jsx(BaseInput, {
      id: id,
      style: {
        '--input-hover-bg': 'transparent',
        '--input-focus-bg': 'transparent'
      },
      testId: postfixTestId(testId, 'input'),
      className: cn.comboInput,
      role: "combobox",
      ref: element => {
        refInput.current = element;
        setRef(inputRef, element);
      },
      suggestionsRef: suggestionsRef,
      value: inputValue,
      customTooltip: tooltip,
      onChange: onChange,
      onCommit: onCommit,
      onBlur: onBlur,
      onCancel: onCancel,
      onFocus: () => {
        setIsActive(true);
        suggestionsRef.current?.closeSuggestions();
        props.onFocus?.();
      },
      editable: editable,
      placeholder: placeholder,
      suggestionItems: items,
      enableAutoComplete: false,
      required: required,
      type: type,
      filterType: filterType,
      status: isDropdownOpening.current === true ? undefined : getStatus(),
      message: getMessage(),
      ariaLabel: ariaLabel,
      ariaDescribedBy: comboAriaDescribedBy,
      ariaLabelledBy: ariaLabelledBy,
      ariaInvalid: comboStatus === 'alert' || !isValid,
      hideBorder: true,
      maxLength: maxLength,
      commitOnEnterInDialog: commitOnEnterInDialog,
      enableVirtualScrolling: enableVirtualScrolling,
      inputContainerWidth: inputContainerWidth,
      disabled: disabled,
      displayListItemIcon: props.displayListItemIcon
    }), renderSlotWithComponentFallback(renderIcon, Icon, {
      className: cn.comboboxIcon,
      testId: postfixTestId(testId, 'icon'),
      icon: DisclosureExpand,
      tabIndex: -1,
      ref: element => {
        buttonRef.current = element;
      }
    })]
  });
});
//# sourceMappingURL=BaseInputBox.js.map